/**
 * Formats duration into HHh MMm SSs or DDd HHh MMm SSs.
 * @param {number | string | null | undefined} value - Duration value.
 * @param {object} options - Formatting options.
 * @param {string} [options.defaultValue] - Default value for 0, negative, or invalid duration.
 * @param {boolean} [options.showDays] - Whether to include days in the output.
 * @param {'hr' | 'min' | 'sec'} [options.formatBy] - Unit of the input value.
 * @returns {string} Formatted duration.
 */
function FormatDuration(
  value: number | string | null | undefined,
  {
    defaultValue,
    showDays = false,
    formatBy = 'hr',
  }: {
    defaultValue?: string;
    showDays?: boolean;
    formatBy?: 'hr' | 'min' | 'sec';
  } = {},
): string {

  const defaultOutput = defaultValue ?? (showDays ? '00d 00h 00m 00s' : '00h 00m 00s');
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return defaultOutput;
  }

  const unitToSeconds = {
    hr: 3600,
    min: 60,
    sec: 1,
  } as const;

  const totalSeconds = Math.round(numericValue * unitToSeconds[formatBy]);

  if (totalSeconds <= 0) {
    return defaultOutput;
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  if (!showDays) {
    return `${String(hours).padStart(2, '0')}h ${formattedMinutes}m ${formattedSeconds}s`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  return `${String(days).padStart(2, '0')}d ${String(remainingHours).padStart(2, '0')}h ${formattedMinutes}m ${formattedSeconds}s`;
}

export default FormatDuration;
